import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { validateUsername, getUsernameRequirements } from '../utils/usernameValidator';
import firebaseService from '../services/firebaseService'; // For creating user document and checking username
import { useAuth } from '../contexts/AuthContext'; // To update currentUser after profile completion
import './AuthScreen.css'; // Reusing some styles from AuthScreen

const CompleteProfileScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, refreshUser, completeProfile, signOut } = useAuth(); // Use completeProfile instead of direct firebaseService calls
  const { user: googleUser, isNewUser } = location.state || {}; // Data passed from GoogleSignInButton

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    tosAccepted: false,
  });

  // Populate form data when googleUser or currentUser changes
  useEffect(() => {
    if (googleUser || currentUser) {
      const user = googleUser || currentUser;
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName || (user.displayName ? user.displayName.split(' ')[0] : ''),
        lastName: prev.lastName || (user.displayName ? user.displayName.split(' ').slice(1).join(' ') : ''),
        email: prev.email || user.email || '',
      }));
    }
  }, [googleUser, currentUser]);
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  // Redirect if not a new Google user or if already logged in and profile complete
  useEffect(() => {
    // If we have state from GoogleSignInButton, use it
    if (isNewUser && googleUser?.uid) {
      return;
    }

    // If we don't have state, check if we have a currentUser (e.g. on refresh)
    if (currentUser) {
      if (currentUser.profileComplete) {
        navigate('/app', { replace: true });
      }
      // If not complete, we stay here and use currentUser data
      return;
    }

    // If neither state nor currentUser exists, redirect to auth
    toast.error('Please sign in to complete your profile.');
    navigate('/auth', { replace: true });
  }, [isNewUser, googleUser, currentUser, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'username') {
      setUsernameError(''); // Clear error on input change
    }
  };

  const handleUsernameBlur = async () => {
    if (!formData.username) {
      setUsernameError('Username is required.');
      return;
    }
    const validation = validateUsername(formData.username);
    if (!validation.isValid) {
      setUsernameError(validation.error);
      return;
    }

    setLoading(true);
    try {
      const existingUser = await firebaseService.getUserByUsername(formData.username);
      if (existingUser) {
        setUsernameError('This username is already taken.');
      } else {
        setUsernameError('');
      }
    } catch (error) {
      console.error("Error checking username uniqueness:", error);
      setUsernameError('Error checking username. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await signOut();
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error('Failed to log out.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Re-validate everything before submission
    if (!formData.username) {
      toast.error('Username is required.');
      setLoading(false);
      return;
    }
    const validation = validateUsername(formData.username);
    if (!validation.isValid) {
      toast.error(validation.error);
      setLoading(false);
      return;
    }
    const existingUser = await firebaseService.getUserByUsername(formData.username);
    if (existingUser) {
      toast.error('This username is already taken.');
      setLoading(false);
      return;
    }
    // if (!formData.tosAccepted) {
    //   toast.error('You must accept the Terms of Service.');
    //   setLoading(false);
    //   return;
    // }

    const uid = googleUser?.uid || currentUser?.uid;
    if (!uid) {
      toast.error('User session lost. Please sign in again.');
      navigate('/auth');
      return;
    }

    try {
      await completeProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username.toLowerCase().trim(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone,
      });

      toast.success('Profile created successfully! Welcome to The Crapper Mapper! 💩');
      
      // Small delay to ensure state is propagated before navigation
      setTimeout(() => {
        navigate('/app', { replace: true });
      }, 100);
    } catch (error) {
      console.error("Error completing profile:", error);
      toast.error(error.message || 'Failed to complete profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/app-icon.png" alt="The Crapper Mapper" />
          </div>
          <h1>Complete Your Profile</h1>
          <p>Just a few more details to get started!</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="First name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Last name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              onBlur={handleUsernameBlur}
              placeholder="Choose a fun username"
              maxLength={30}
              required
            />
            <small className="form-help-text">
              {getUsernameRequirements().description}
            </small>
            {usernameError && (
              <small className="error-text">{usernameError}</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              readOnly // Email from Google should not be editable
              disabled
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
            />
          </div>

          {/*<div className="form-group checkbox-group">*/}
          {/*  <input*/}
          {/*    type="checkbox"*/}
          {/*    id="tosAccepted"*/}
          {/*    name="tosAccepted"*/}
          {/*    checked={formData.tosAccepted}*/}
          {/*    onChange={handleInputChange}*/}
          {/*    required*/}
          {/*  />*/}
          {/*  <label htmlFor="tosAccepted">I accept the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Terms of Service</a></label>*/}
          {/*</div>*/}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={loading || !!usernameError }
          >
            {loading ? 'Saving Profile...' : 'Complete Profile'}
          </button>

          <button
            type="button"
            className="btn btn-outline w-full"
            onClick={handleCancel}
            style={{ marginTop: '10px' }}
          >
            Cancel and Log Out
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfileScreen;
